import { useState } from "react";
import {
  CalendarCheck,
  Sparkles,
  TrendingUp,
  UserPlus,
  Wrench,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { TreasuryWidget } from "../finance/TreasuryWidget";
import { ChurnBanner } from "./ChurnBanner";
import { EmailEnrichmentCard } from "./EmailEnrichmentCard";
import { InstantlyBanner } from "./InstantlyBanner";
import { MeetingBriefsTodayCard } from "./MeetingBriefsTodayCard";
import { MyDayActionsList } from "./MyDayActionsList";
import { MyDayCompaniesToClose } from "./MyDayCompaniesToClose";
import { MyDayContactsToRelance } from "./MyDayContactsToRelance";
import { NewSignups } from "./NewSignups";
import { PostMeetingRecapCard } from "./PostMeetingRecapCard";
import { QuickAddActionDialog } from "./QuickAddActionDialog";
import { QuickAddContactButton } from "./QuickAddContactButton";
import { RelanceNewSignupsCard } from "./RelanceNewSignupsCard";
import { RevenueTopAccountsCard } from "./RevenueTopAccountsCard";
import { SignupQualifCard } from "./SignupQualifCard";
import { WarmReawakeningCard } from "./WarmReawakeningCard";

// Sprint 9.2 + 10.x — Vue commerciale de /today.
// 4 onglets façon DevPageHead pour réduire la charge visuelle :
// Mon jour · Pipeline · Nouveaux inscrits · Outils IA.
// Les banners (Instantly, Churn) restent en tête comme alertes globales.

type TabId = "today" | "pipeline" | "signups" | "tools";

const TABS: { id: TabId; label: string; icon: typeof CalendarCheck }[] = [
  { id: "today", label: "Mon jour", icon: CalendarCheck },
  { id: "pipeline", label: "Pipeline", icon: TrendingUp },
  { id: "signups", label: "Nouveaux inscrits", icon: UserPlus },
  { id: "tools", label: "Outils IA", icon: Wrench },
];

export const TodayCommercialView = () => {
  const [tab, setTab] = useState<TabId>("today");

  return (
    <div className="flex flex-col gap-3">
      {/* Quick actions */}
      <div className="flex items-center justify-end gap-2">
        <QuickAddActionDialog />
        <QuickAddContactButton />
      </div>

      {/* Argent + alertes — toujours visibles, hors onglets.
          TreasuryWidget n'était monté que sur /my-day, route absente de la
          navigation : Stripe encaissait et aucune surface atteignable ne
          l'affichait. Il rend null si l'utilisateur n'est pas admin. */}
      <div className="flex flex-col gap-2">
        <TreasuryWidget />
        <InstantlyBanner />
        <ChurnBanner />
      </div>

      {/* Tabs toolbar — style aligné DevPageHead */}
      <div className="flex items-center gap-1 border-b border-border pb-0 -mb-px overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                "h-9 px-3.5 rounded-t-md text-[13px] font-medium inline-flex items-center gap-2 border-b-2 transition-colors shrink-0",
                isActive
                  ? "border-fabrik-violet text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40",
              )}
            >
              <Icon className="size-3.5" />
              <span>{t.label}</span>
              {t.id === "tools" && (
                <Sparkles className="size-3 text-fabrik-violet" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content — toutes les tabs restent montées (CSS hide/show)
          pour préserver l'état (runs en cours, drafts pending, briefs
          générés) entre les changements d'onglet. */}
      <div className={cn(tab !== "today" && "hidden")}>
        <div className="flex flex-col gap-3">
          <MyDayActionsList />
          <MyDayContactsToRelance />
        </div>
      </div>

      <div className={cn(tab !== "pipeline" && "hidden")}>
        <div className="flex flex-col gap-3">
          <RevenueTopAccountsCard />
          <MyDayCompaniesToClose />
        </div>
      </div>

      <div className={cn(tab !== "signups" && "hidden")}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <RelanceNewSignupsCard />
          <NewSignups />
        </div>
      </div>

      <div className={cn(tab !== "tools" && "hidden")}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <MeetingBriefsTodayCard />
          <PostMeetingRecapCard />
          <WarmReawakeningCard />
          <SignupQualifCard />
          <EmailEnrichmentCard />
        </div>
      </div>
    </div>
  );
};
